import React, { useState, useEffect } from "react";
import axios from "../../utils/api";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  CardMedia,
  Box,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Skeleton,
  Chip,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const accent = "#0d47a1";
const accentLight = "rgba(13, 71, 161, 0.08)";

const RobotList = () => {
  const { email } = useParams();
  const [robots, setRobots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [robotToDelete, setRobotToDelete] = useState(null);
  const [enteredRobotId, setEnteredRobotId] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRobot, setEditRobot] = useState(null);

  const navigate = useNavigate();

  const decodeEmail = (encodedEmail) => {
    return atob(decodeURIComponent(encodedEmail));
  };

  useEffect(() => {
    const fetchRobots = async () => {
      try {
        const decodedEmail = decodeEmail(email);
        const response = await axios.get(`/getrobots/${decodedEmail}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setRobots(response.data ?? []);
      } catch (error) {
        console.error("Error fetching robots:", error);
        setRobots([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRobots();
  }, [email]);

  const handleDelete = async () => {
    if (enteredRobotId !== robotToDelete) {
      setSnackbarMessage("Robot ID does not match.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    try {
      await axios.delete(`/robots-delete/${robotToDelete}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setRobots(robots.filter((robot) => robot.robotId !== robotToDelete));
      setSnackbarMessage("Robot deleted successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error deleting robot:", error);
      setSnackbarMessage("Failed to delete robot.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setDialogOpen(false);
      setEnteredRobotId("");
    }
  };

  const handleEdit = (robot) => {
    setEditRobot({ ...robot });
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`/robots-update/${editRobot.robotId}`, editRobot, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setRobots(
        robots.map((robot) =>
          robot.robotId === editRobot.robotId ? editRobot : robot
        )
      );
      setSnackbarMessage("Robot updated successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setEditDialogOpen(false);
    } catch (error) {
      console.error("Error updating robot:", error);
      setSnackbarMessage("Failed to update robot.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const getStatusConfig = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "active")
      return {
        icon: <CheckCircleOutlinedIcon sx={{ fontSize: 16 }} />,
        bg: "rgba(46, 125, 50, 0.12)",
        color: "#2e7d32",
      };
    if (s === "warning")
      return {
        icon: <WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />,
        bg: "rgba(237, 108, 2, 0.12)",
        color: "#ed6c02",
      };
    if (s === "error")
      return {
        icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />,
        bg: "rgba(211, 47, 47, 0.12)",
        color: "#d32f2f",
      };
    return {
      icon: <CheckCircleOutlinedIcon sx={{ fontSize: 16 }} />,
      bg: "rgba(13, 71, 161, 0.12)",
      color: accent,
    };
  };

  if (loading) {
    return (
      <Box sx={styles.pageWrap}>
        <Box sx={styles.innerWrap}>
          <Box sx={styles.headerSkeleton}>
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton variant="text" width={280} height={40} />
          </Box>
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item key={i} xs={12} sm={6} md={4}>
                <Skeleton
                  variant="rounded"
                  height={320}
                  sx={{ borderRadius: 3 }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.pageWrap}>
      <Box sx={styles.innerWrap}>
        {/* Header */}
        <Box sx={styles.header}>
          <IconButton
            onClick={() => navigate(-1)}
            size="medium"
            sx={styles.backButton}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={styles.title}>
              Robots for {decodeEmail(email)}
            </Typography>
            <Typography sx={styles.subtitle}>
              View and manage robots assigned to this user
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        {robots.length === 0 ? (
          <Box sx={styles.emptyState}>
            <SmartToyOutlinedIcon sx={styles.emptyIcon} />
            <Typography sx={styles.emptyTitle}>No robots assigned</Typography>
            <Typography sx={styles.emptySubtitle}>
              There are no robots assigned to this user yet.
            </Typography>
          </Box>
        ) : robots.length === 1 ? (
          <Box sx={styles.singleCardWrap}>
            <Box sx={styles.singleCardInner}>
              <RobotCard
                robot={robots[0]}
                index={0}
                getStatusConfig={getStatusConfig}
                onEdit={handleEdit}
                onDeleteClick={(robotId) => {
                  setRobotToDelete(robotId);
                  setDialogOpen(true);
                }}
              />
            </Box>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {robots.map((robot, index) => (
              <Grid item key={robot.robotId || index} xs={12} md={6} lg={6}>
                <RobotCard
                  robot={robot}
                  index={index}
                  getStatusConfig={getStatusConfig}
                  onEdit={handleEdit}
                  onDeleteClick={(robotId) => {
                    setRobotToDelete(robotId);
                    setDialogOpen(true);
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEnteredRobotId("");
        }}
        PaperProps={{ sx: styles.dialogPaper }}
        sx={styles.dialogRoot}
      >
        <Box sx={styles.dialogDeleteHeader}>
          <Box sx={styles.dialogIconCircleDelete}>
            <DeleteOutlinedIcon sx={{ fontSize: 28, color: "#fff" }} />
          </Box>
          <Typography sx={styles.dialogTitleMain}>Confirm delete</Typography>
          <Typography sx={styles.dialogTitleSub}>
            This action cannot be undone.
          </Typography>
        </Box>
        <DialogContent sx={styles.dialogContent}>
          <Typography sx={styles.dialogSubtitle}>
            To confirm, enter the robot ID exactly as shown below.
          </Typography>
          <Box sx={styles.dialogRobotIdBox}>
            <Typography sx={styles.dialogLabel}>Robot ID to delete</Typography>
            <Typography sx={styles.dialogRobotIdValue}>{robotToDelete}</Typography>
          </Box>
          <TextField
            fullWidth
            size="medium"
            placeholder="Type robot ID to confirm"
            value={enteredRobotId}
            onChange={(e) => setEnteredRobotId(e.target.value)}
            sx={styles.dialogInput}
            variant="outlined"
            InputProps={{ sx: { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={styles.dialogActions}>
          <Button
            onClick={() => {
              setDialogOpen(false);
              setEnteredRobotId("");
            }}
            sx={styles.dialogCancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            sx={styles.dialogDelete}
          >
            Delete robot
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit robot dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        PaperProps={{ sx: { ...styles.dialogPaper, maxWidth: 460 } }}
        sx={styles.dialogRoot}
      >
        <Box sx={styles.dialogEditHeader}>
          <Box sx={styles.dialogIconCircleEdit}>
            <EditOutlinedIcon sx={{ fontSize: 26, color: "#fff" }} />
          </Box>
          <Box>
            <Typography sx={styles.dialogTitleMain}>Edit robot details</Typography>
            <Typography sx={styles.dialogTitleSub}>
              Update IP address and amps for this robot.
            </Typography>
          </Box>
        </Box>
        <DialogContent sx={styles.dialogContentEdit}>
          {editRobot && (
            <Box sx={styles.editFields}>
              <Box sx={styles.editFieldWrap}>
                <Typography sx={styles.editFieldLabel}>IP Address</Typography>
                <TextField
                  fullWidth
                  size="medium"
                  value={editRobot.IPAddress ?? ""}
                  onChange={(e) =>
                    setEditRobot({ ...editRobot, IPAddress: e.target.value })
                  }
                  sx={styles.editFieldInput}
                  variant="outlined"
                  placeholder="e.g. ws://192.168.1.1:9090"
                  InputProps={{ sx: { borderRadius: 2 } }}
                />
              </Box>
              <Box sx={styles.editFieldWrap}>
                <Typography sx={styles.editFieldLabel}>Amps</Typography>
                <TextField
                  fullWidth
                  size="medium"
                  value={editRobot.amps ?? ""}
                  onChange={(e) =>
                    setEditRobot({ ...editRobot, amps: e.target.value })
                  }
                  sx={styles.editFieldInput}
                  variant="outlined"
                  placeholder="e.g. 72"
                  InputProps={{ sx: { borderRadius: 2 } }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={styles.dialogActions}>
          <Button onClick={() => setEditDialogOpen(false)} sx={styles.dialogCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            sx={styles.dialogUpdate}
          >
            Save changes
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
          sx={{ width: "100%", borderRadius: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

function MetaRow({ label, value }) {
  return (
    <Box sx={metaRowStyles.row}>
      <Typography sx={metaRowStyles.label}>{label}</Typography>
      <Typography sx={metaRowStyles.value} title={value}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

const metaRowStyles = {
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 1.5,
    py: 0.75,
    borderBottom: "1px solid rgba(0,0,0,0.04)",
    "&:last-of-type": { borderBottom: "none" },
  },
  label: {
    fontSize: "0.8125rem",
    color: "#64748b",
    fontWeight: 500,
    flexShrink: 0,
  },
  value: {
    fontSize: "0.875rem",
    color: "#1e293b",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "break-word",
    maxWidth: "65%",
    textAlign: "right",
  },
};

const styles = {
  pageWrap: {
    minHeight: "100vh",
    bgcolor: "#fafbfc",
    py: { xs: 2, md: 4 },
    px: { xs: 2, sm: 3, md: 4 },
    width: "100%",
    boxSizing: "border-box",
  },
  innerWrap: {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  headerSkeleton: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    mb: 3,
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 2,
    mb: 3,
  },
  backButton: {
    bgcolor: "white",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    "&:hover": {
      bgcolor: "#f5f5f5",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
  },
  title: {
    fontWeight: 700,
    color: "#1a1a2e",
    fontSize: { xs: "1.25rem", md: "1.5rem" },
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  subtitle: {
    color: "#64748b",
    fontSize: "0.875rem",
    mt: 0.5,
  },
  emptyState: {
    textAlign: "center",
    py: 8,
    px: 2,
    bgcolor: "white",
    borderRadius: 3,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  emptyIcon: {
    fontSize: 56,
    color: "#cbd5e1",
    mb: 2,
  },
  emptyTitle: {
    color: "#475569",
    fontWeight: 600,
    fontSize: "1.125rem",
  },
  emptySubtitle: {
    color: "#94a3b8",
    fontSize: "0.875rem",
    mt: 1,
  },
  singleCardWrap: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
  singleCardInner: {
    width: "100%",
    maxWidth: 640,
  },
  card: {
    borderRadius: 3,
    overflow: "hidden",
    bgcolor: "white",
    boxShadow: "none",
    border: "1px solid rgba(0,0,0,0.06)",
    minHeight: 320,
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
  },
  cardLeft: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  cardRight: {
    position: "relative",
    width: { xs: "100%", sm: 280 },
    minHeight: { xs: 240, sm: 280 },
    flexShrink: 0,
    bgcolor: "#f1f5f9",
  },
  cardImage: {
    width: "100%",
    height: "100%",
    minHeight: 280,
    objectFit: "cover",
  },
  cardImagePlaceholder: {
    width: "100%",
    height: "100%",
    minHeight: 280,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    bgcolor: "#f1f5f9",
  },
  placeholderIcon: {
    fontSize: 72,
    color: "#cbd5e1",
  },
  statusChip: {
    position: "absolute",
    top: 12,
    right: 12,
    fontWeight: 600,
    fontSize: "0.75rem",
    "& .MuiChip-icon": {
      color: "inherit",
    },
  },
  statusChipInline: {
    mt: 1,
    fontWeight: 600,
    fontSize: "0.75rem",
    "& .MuiChip-icon": {
      color: "inherit",
    },
  },
  cardContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    py: 2.5,
    px: 3,
    "&:last-child": { pb: 2.5 },
  },
  robotName: {
    fontWeight: 700,
    fontSize: "1.2rem",
    color: "#1e293b",
    mb: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  metaGrid: {
    flex: 1,
    minHeight: 0,
  },
  actions: {
    display: "flex",
    gap: 1.5,
    mt: 2,
    pt: 2,
    borderTop: "1px solid rgba(0,0,0,0.06)",
  },
  editButton: {
    textTransform: "none",
    color: accent,
    fontSize: "0.875rem",
    "&:hover": {
      bgcolor: accentLight,
    },
  },
  deleteButton: {
    textTransform: "none",
    color: "#d32f2f",
    fontSize: "0.875rem",
    "&:hover": {
      bgcolor: "rgba(211, 47, 47, 0.08)",
    },
  },
  dialogRoot: {
    "& .MuiBackdrop-root": {
      bgcolor: "rgba(15, 23, 42, 0.4)",
      backdropFilter: "blur(2px)",
    },
  },
  dialogPaper: {
    borderRadius: 4,
    p: 0,
    maxWidth: 420,
    bgcolor: "#ffffff",
    border: "1px solid rgba(0,0,0,0.08)",
    boxShadow: "0 24px 48px -12px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },
  dialogDeleteHeader: {
    px: 3,
    pt: 3,
    pb: 2,
    textAlign: "center",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    bgcolor: "#fffbfa",
  },
  dialogEditHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: 2,
    px: 3,
    pt: 3,
    pb: 2,
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    bgcolor: "#f8fafc",
  },
  dialogIconCircleDelete: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    bgcolor: "#d32f2f",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 1rem",
    boxShadow: "0 4px 14px rgba(211, 47, 47, 0.35)",
  },
  dialogIconCircleEdit: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    bgcolor: accent,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 4px 14px rgba(13, 71, 161, 0.35)",
  },
  dialogTitleMain: {
    fontWeight: 700,
    fontSize: "1.25rem",
    color: "#1a1a2e",
    lineHeight: 1.3,
  },
  dialogTitleSub: {
    fontSize: "0.875rem",
    color: "#64748b",
    mt: 0.5,
    lineHeight: 1.4,
  },
  dialogContent: {
    px: 3,
    pt: 2.5,
    pb: 2,
    overflow: "visible",
  },
  dialogContentEdit: {
    px: 3,
    pt: 2.5,
    pb: 2,
    overflow: "visible",
  },
  dialogSubtitle: {
    fontSize: "0.9375rem",
    color: "#475569",
    mb: 2,
    lineHeight: 1.5,
  },
  dialogRobotIdBox: {
    mb: 2,
    p: 2,
    borderRadius: 2,
    bgcolor: "#fef2f2",
    border: "1px solid rgba(211, 47, 47, 0.2)",
  },
  dialogLabel: {
    fontSize: "0.75rem",
    color: "#64748b",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    mb: 0.75,
  },
  dialogRobotIdValue: {
    fontSize: "1.125rem",
    fontWeight: 700,
    color: "#b91c1c",
    letterSpacing: "0.02em",
  },
  dialogInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: "#ffffff",
      "& fieldset": {
        borderColor: "rgba(0,0,0,0.12)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(0,0,0,0.2)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#1a1a2e",
        borderWidth: "2px",
      },
    },
  },
  dialogActions: {
    px: 3,
    py: 2.5,
    gap: 1.5,
    borderTop: "1px solid rgba(0,0,0,0.06)",
    bgcolor: "#fafbfc",
  },
  dialogCancel: {
    textTransform: "none",
    color: "#64748b",
    fontSize: "0.9375rem",
    fontWeight: 600,
    borderRadius: 2,
    px: 2.5,
    "&:hover": {
      bgcolor: "rgba(0,0,0,0.05)",
    },
  },
  dialogDelete: {
    textTransform: "none",
    fontSize: "0.9375rem",
    fontWeight: 600,
    borderRadius: 2,
    px: 3,
    py: 1.25,
    bgcolor: "#d32f2f",
    boxShadow: "0 2px 8px rgba(211, 47, 47, 0.3)",
    "&:hover": {
      bgcolor: "#b71c1c",
      boxShadow: "0 4px 12px rgba(211, 47, 47, 0.4)",
    },
  },
  dialogUpdate: {
    textTransform: "none",
    fontSize: "0.9375rem",
    fontWeight: 600,
    borderRadius: 2,
    px: 3,
    py: 1.25,
    bgcolor: accent,
    boxShadow: "0 2px 8px rgba(13, 71, 161, 0.3)",
    "&:hover": {
      bgcolor: "#0a3d91",
      boxShadow: "0 4px 12px rgba(13, 71, 161, 0.4)",
    },
  },
  editFields: {
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
  },
  editFieldWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 0.75,
  },
  editFieldLabel: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: "#374151",
  },
  editFieldInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: "#ffffff",
      "& fieldset": {
        borderColor: "rgba(0,0,0,0.12)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(0,0,0,0.2)",
      },
      "&.Mui-focused fieldset": {
        borderColor: accent,
        borderWidth: "2px",
      },
    },
  },
};

function RobotCard({ robot, index, getStatusConfig, onEdit, onDeleteClick }) {
  const statusConfig = getStatusConfig(robot.status);
  return (
    <Card elevation={0} sx={styles.card}>
      <Box sx={styles.cardLeft}>
        <CardContent sx={styles.cardContent}>
          <Typography sx={styles.robotName}>
            {robot.username || "—"}
          </Typography>
          <Box sx={styles.metaGrid}>
            <MetaRow label="Robot ID" value={robot.robotId} />
            <MetaRow label="Model" value={robot.model} />
            <MetaRow label="Serial" value={robot.serialNumber} />
            <MetaRow label="IP" value={robot.IPAddress} />
            <MetaRow
              label="Location"
              value={`${robot.location || ""} ${robot.subLocation || ""}`.trim()}
            />
            <MetaRow label="Amps" value={robot.amps} />
            <MetaRow
              label="Initialized"
              value={
                robot.robotInitializeDate
                  ? new Date(
                      robot.robotInitializeDate
                    ).toLocaleDateString()
                  : "—"
              }
            />
            <MetaRow
              label="Last maintenance"
              value={
                robot.lastMaintenanceDate
                  ? new Date(
                      robot.lastMaintenanceDate
                    ).toLocaleDateString()
                  : "—"
              }
            />
          </Box>
          {!robot.image && (
            <Chip
              size="small"
              icon={statusConfig.icon}
              label={robot.status || "—"}
              sx={{
                ...styles.statusChipInline,
                bgcolor: statusConfig.bg,
                color: statusConfig.color,
              }}
            />
          )}
          <Box sx={styles.actions}>
            <Button
              startIcon={<EditOutlinedIcon />}
              onClick={() => onEdit(robot)}
              size="small"
              sx={styles.editButton}
            >
              Edit
            </Button>
            <Button
              startIcon={<DeleteOutlinedIcon />}
              onClick={() => onDeleteClick(robot.robotId)}
              size="small"
              sx={styles.deleteButton}
            >
              Delete
            </Button>
          </Box>
        </CardContent>
      </Box>
      <Box sx={styles.cardRight}>
        {robot.image ? (
          <>
            <CardMedia
              component="img"
              image={robot.image}
              alt={robot.username}
              sx={styles.cardImage}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <Chip
              size="small"
              icon={statusConfig.icon}
              label={robot.status || "—"}
              sx={{
                ...styles.statusChip,
                bgcolor: statusConfig.bg,
                color: statusConfig.color,
              }}
            />
          </>
        ) : (
          <Box sx={styles.cardImagePlaceholder}>
            <SmartToyOutlinedIcon sx={styles.placeholderIcon} />
            <Chip
              size="small"
              icon={statusConfig.icon}
              label={robot.status || "—"}
              sx={{
                ...styles.statusChip,
                bgcolor: statusConfig.bg,
                color: statusConfig.color,
              }}
            />
          </Box>
        )}
      </Box>
    </Card>
  );
}

export default RobotList;
